<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';
	import Hls from 'hls.js';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import {
		appendSignedQuery,
		createResignGuard,
		getSignedQuery,
	} from 'src/services/cam-cloud/media-auth';
	import VideoContainer from './VideoContainer.svelte';
	import type { VideoContainerState } from '../types/video-container';
	import { createRecordingLoader } from '../helpers/hls-loader';
	import { deriveVideoKey } from '../helpers/encryption/recording-key';
	import { hasPreservedEraKey, resolveMasterKey } from '../helpers/encryption/master-key-management';
	import { bytesToHex } from '../helpers/encryption/encoding';
	import { camera_storage_passphrases } from '../stores/camera_storage_passphrases';

	export let imagePoster: string = load_image_from('images/device_images/logo_white_S.png');
	/** `.m3u8` playlist URL served from cloud storage (authenticated via GCS signed URL). */
	export let videoSrc: string = '';
	export let encrypted: boolean = false;
	export let keyUri: string = '';
	/** Device id used to look up / persist the decryption passphrase. */
	export let deviceId: string = '';
	/** Recording id used as the HKDF info to derive this recording's media key. */
	export let recordingId: string = '';
	/** Recording ISO date; selects which key **era** (backup) decrypts this recording. */
	export let recordingDate: string = '';
	export let borderRadius: 'small' | 'medium' | 'big' | 'extra-big' = 'extra-big';

	/** Asks an ancestor to show the shared password prompt; `wrongKey` flags a failed decryption. */
	const dispatch = createEventDispatcher<{
		requirekey: { deviceId: string; wrongKey: boolean; date: string };
	}>();

	/** Max automatic recoveries before surfacing a fatal error (prevents tight retry loops). */
	const MAX_RECOVERY_ATTEMPTS = 3;

	/** The part of hls.js's error payload this player reacts to. */
	type HlsErrorData = {
		type: string;
		details: string;
		fatal: boolean;
		url?: string;
		response?: { code?: number; text?: string };
	};

	let videoElement: HTMLVideoElement = null;
	let hls: Hls | null = null;
	let mounted = false;
	/** Guards `initPlayer` from resuming (and creating an orphaned Hls) after teardown. */
	let destroyed = false;
	let isLoading = true;
	let needsKey = false;
	/** Carries the wrong/rotated-key flag to the prompt when the user clicks Unlock. */
	let pendingWrongKey = false;
	let errorState: VideoContainerState = null;
	let recoveryAttempts = 0;
	/** Ensures a single fallback prompt per recording (no blind loop over other keys). */
	let promptedForRecording = false;
	let recordingIdentity = '';
	/** The signed query the current storage requests carry, for a targeted re-sign. */
	let signedQueryInUse: string | null = null;
	const resignMedia = createResignGuard();

	$: state = computeState(isLoading, needsKey, errorState, videoSrc);

	// Reset the single-prompt guard whenever the recording (or its era) changes, so
	// each recording gets its own one-shot fallback prompt.
	$: {
		const identity = `${videoSrc}|${recordingId}|${recordingDate}`;
		if (identity !== recordingIdentity) {
			recordingIdentity = identity;
			promptedForRecording = false;
		}
	}

	/** Flips to `'1'` once this recording's era is unlocked, re-triggering init.
	 * Reading `$camera_storage_passphrases` keeps it reactive. */
	$: eraUnlocked =
		$camera_storage_passphrases && hasPreservedEraKey(deviceId, recordingDate) ? '1' : '';
	$: reinitToken = `${videoSrc}|${encrypted}|${keyUri}|${deviceId}|${recordingId}|${recordingDate}|${eraUnlocked}`;
	$: if (mounted && reinitToken) {
		initPlayer();
	}

	const computeState = (
		loading: boolean,
		locked: boolean,
		err: VideoContainerState,
		src: string,
	): VideoContainerState => {
		if (err) return err;
		if (locked) {
			return {
				type: 'error',
				icon: 'fal fa-lock',
				heading: $format('camera_recording_encrypted', {
					default: 'Encrypted recording',
				}),
				cta: { title: $format('camera_recording_key_submit', { default: 'Unlock' }) },
			};
		}
		if (!src) {
			return {
				type: 'error',
				icon: 'fal fa-video',
				heading: $format('video_player_no_video_heading', { default: 'No Video Available' }),
			};
		}
		if (loading) return { type: 'loading' };
		return null;
	};

	const setError = (heading: string) => {
		errorState = {
			type: 'error',
			icon: 'fal fa-video-slash',
			heading,
			cta: { title: $format('button_retry', { default: 'Retry' }) },
		};
		isLoading = false;
	};

	const destroyHls = () => {
		if (hls) {
			hls.destroy();
			hls = null;
		}
		if (videoElement) {
			videoElement.removeAttribute('src');
			videoElement.load();
		}
	};

	/**
	 * Shows the locked state without auto-opening the shared prompt (recordings can
	 * span eras with different passwords). The user opens it via the Unlock cta
	 * (`handleRetry`); the preserved password is kept for other recordings.
	 */
	const promptForKey = (wrongKey: boolean) => {
		destroyHls();
		pendingWrongKey = wrongKey;
		needsKey = true;
		isLoading = false;
	};

	/**
	 * A decrypt/parse/key failure means the era's key is wrong for this recording.
	 * Surface a single fallback prompt; a fresh password re-arms playback reactively.
	 */
	const handleEncryptedKeyFailure = () => {
		if (promptedForRecording) {
			setError($format('video_player_error_heading', { default: 'Unable to play video' }));
			return;
		}
		promptedForRecording = true;
		promptForKey(true);
	};

	/**
	 * Whether a failed storage request looks like the signed query was rejected.
	 *
	 * @remarks
	 * A missing code counts: GCS answers a `URLPrefix`/`Expires` mismatch with a 403
	 * that carries no `Access-Control-Allow-Origin`, so the browser blocks the
	 * response and hls.js sees status 0.
	 */
	const isSignedQueryRejection = (code?: number): boolean =>
		!code || code === 401 || code === 403 || code === 404;

	/**
	 * Whether the manifest itself never loaded. `startLoad()` resumes an existing
	 * stream, so with no parsed manifest there is nothing to resume: retrying would
	 * leave the player loading forever instead of surfacing the failure.
	 */
	const isManifestFailure = (details: string): boolean =>
		details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
		details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT ||
		details === Hls.ErrorDetails.MANIFEST_PARSING_ERROR;

	/** hls.js recovery for a fatal error, capped so a persistent fault cannot loop. */
	const recoverOrFail = ({ type, details }: HlsErrorData) => {
		const failed = $format('video_player_error_heading', { default: 'Unable to play video' });

		if (isManifestFailure(details) || recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
			setError(failed);
			return;
		}
		recoveryAttempts += 1;

		switch (type) {
			case Hls.ErrorTypes.NETWORK_ERROR:
				hls?.startLoad();
				break;
			case Hls.ErrorTypes.MEDIA_ERROR:
				hls?.recoverMediaError();
				break;
			default:
				setError(failed);
		}
	};

	/**
	 * Rebuilds the player with a fresh signed query, resuming where it left off.
	 * Once this recording has been re-signed the failure is not an auth one, so it
	 * falls back to the plain recovery.
	 */
	const resignAndReload = async (data: HlsErrorData) => {
		const resumeAt = videoElement?.currentTime ?? 0;
		const freshQuery = await resignMedia(videoSrc, signedQueryInUse);
		if (destroyed) return;

		if (!freshQuery) {
			recoverOrFail(data);
			return;
		}
		await initPlayer(resumeAt);
	};

	const handleHlsError = (_event: unknown, data: HlsErrorData) => {
		console.warn('[HLSPlayer] hls error', {
			type: data.type,
			details: data.details,
			fatal: data.fatal,
			code: data.response?.code,
			url: data.url,
			encrypted,
		});

		const keyProblem =
			data.details === Hls.ErrorDetails.KEY_LOAD_ERROR ||
			data.details === Hls.ErrorDetails.FRAG_DECRYPT_ERROR ||
			data.details === Hls.ErrorDetails.KEY_LOAD_TIMEOUT;
		if (keyProblem) {
			handleEncryptedKeyFailure();
			return;
		}

		// A wrong key decrypts to garbage, so the demuxer reports a parse error.
		if (encrypted && data.details === Hls.ErrorDetails.FRAG_PARSING_ERROR) {
			handleEncryptedKeyFailure();
			return;
		}

		if (!data.fatal) return;

		// A stale query fails every storage request and hls.js cannot swap it in
		// place, so the player is rebuilt with a fresh one instead of retrying.
		if (data.type === Hls.ErrorTypes.NETWORK_ERROR && isSignedQueryRejection(data.response?.code)) {
			void resignAndReload(data);
			return;
		}

		recoverOrFail(data);
	};

	/** Dev trace: logs the decryption chain (master key, AES-128 video key). */
	const logRecordingKey = (masterKey: string, keyBytes: Uint8Array | null) => {
		if (!import.meta.env.DEV) return;
		console.log('[recording-decrypt] HLS recording key', {
			deviceId,
			recordingId,
			masterKey,
			videoKeyHex: keyBytes ? bytesToHex(keyBytes) : null,
		});
	};

	/**
	 * Resolves the AES-128 video key: selects the era key for `recordingDate`, derives
	 * `rec_key`, and returns its first 16 bytes. `null` when the era key is unavailable.
	 */
	const resolveVideoKey = async (): Promise<Uint8Array | null> => {
		const masterKey = await resolveMasterKey(deviceId, recordingDate);
		if (!masterKey) return null;
		const keyBytes = await deriveVideoKey(masterKey, deviceId, recordingId);
		logRecordingKey(masterKey, keyBytes);
		return keyBytes;
	};

	/**
	 * Wires up hls.js with the recording loader: signs storage requests and feeds
	 * the derived video key.
	 *
	 * @param resumeAt - Playback position to restore once the manifest is parsed.
	 */
	const setupHls = (
		storageUrl: string,
		signedQuery: string,
		keyBytes: Uint8Array | null,
		resumeAt: number,
	) => {
		let storageOrigin = '';
		try {
			storageOrigin = new URL(storageUrl).origin;
		} catch {
			// malformed storageUrl; signing guard simply won't match
		}

		if (import.meta.env.DEV) {
			console.log('[recording-decrypt] HLS loader input', {
				storageUrl,
				encrypted,
				keyUri,
				videoKeyHex: keyBytes ? bytesToHex(keyBytes) : null,
				signedOrigin: storageOrigin,
				hasSignedQuery: !!signedQuery,
			});
		}

		hls = new Hls({
			loader: createRecordingLoader({
				keyUri,
				keyBytes,
				signedQuery,
				signedOrigin: storageOrigin,
			}),
		});

		hls.on(Hls.Events.ERROR, handleHlsError);
		hls.on(Hls.Events.MANIFEST_PARSED, () => {
			isLoading = false;
			recoveryAttempts = 0;
			if (resumeAt > 0 && videoElement) videoElement.currentTime = resumeAt;
		});
		// The loader signs the manifest and every segment, so the source stays unsigned.
		hls.loadSource(storageUrl);
		hls.attachMedia(videoElement);
	};

	/**
	 * (Re)builds the player for `videoSrc`.
	 *
	 * @param resumeAt - Playback position to restore, used when rebuilding after a
	 * re-sign.
	 */
	const initPlayer = async (resumeAt = 0) => {
		destroyHls();
		errorState = null;
		needsKey = false;
		pendingWrongKey = false;
		recoveryAttempts = 0;

		if (!videoSrc) {
			isLoading = false;
			return;
		}

		// Gate on the decryption key before any network work, so the key prompt
		// isn't blocked by a slow/failing signed-URL request.
		let keyBytes: Uint8Array | null = null;
		if (encrypted) {
			keyBytes = await resolveVideoKey();
			if (destroyed) return;
			if (!keyBytes) {
				promptForKey(false);
				return;
			}
		}

		isLoading = true;

		const storageUrl = videoSrc;
		signedQueryInUse = await getSignedQuery();
		if (destroyed) return;
		// Wait for the loading state to mount the <video> (dropped in blocked states).
		await tick();
		if (!videoElement) {
			isLoading = false;
			return;
		}

		if (Hls.isSupported()) {
			setupHls(storageUrl, signedQueryInUse ?? '', keyBytes, resumeAt);
			return;
		}

		// Native HLS can't inject our derived key, so encrypted streams need hls.js.
		if (!encrypted && videoElement.canPlayType('application/vnd.apple.mpegurl')) {
			videoElement.src = appendSignedQuery(storageUrl, signedQueryInUse) || storageUrl;
			isLoading = false;
			return;
		}

		setError(
			$format('video_player_unsupported', {
				default: 'Playback is not supported on this device.',
			}),
		);
	};

	const handleRetry = () => {
		if (needsKey) {
			dispatch('requirekey', { deviceId, wrongKey: pendingWrongKey, date: recordingDate });
			return;
		}
		initPlayer();
	};

	onMount(() => {
		mounted = true;
	});

	onDestroy(() => {
		destroyed = true;
		destroyHls();
	});
</script>

<VideoContainer {state} on:retry={handleRetry}>
	<video
		poster={imagePoster}
		bind:this={videoElement}
		autoPlay
		playsInline
		muted
		controls
		class="width-full"
		style="border-radius: var(--radius-{borderRadius}); aspect-ratio: 16 / 9;"
	>
	</video>
</VideoContainer>
